import { Inject, Injectable, InjectionToken } from "@angular/core";
import {
  Account,
  AccountConfigurationDto,
  AccountPosSystemSettingsConfiguration,
  allowedClientFeatures,
  CompanyService,
  ExternalFeatureWithDependencyGetDto,
  PermissionCategoryDto,
  PermissionMappingGetDto,
  PermissionTypeDto,
  PlatformService,
  SubscriptionPlanService,
  SubscriptionPlanShortGetDto
} from "clearline-api";
import { AccountService, AppConfigService, AuthBaseService, EnumHelpers, RoleTypes } from "core-kit";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { catchError, filter, map, switchMap, take, tap } from "rxjs/operators";
import { CurrentAccountService } from "./current-account.service";

export const ROUTE_PERMISSIONS_MAP = new InjectionToken<Record<string, PermissionTypeDto>>("ROUTE_PERMISSIONS_MAP");

type MenuItemPermission = Record<PermissionTypeDto, boolean>;

interface MenuItemAvailabilityParams {
  permissions: PermissionTypeDto[];
  account: Account;
  externalFeatures: ExternalFeatureWithDependencyGetDto[];
}

const allowAllAccessControlConfig: MenuItemPermission = generateAccessControlConfig(true);
const disabledAccessControlConfig: MenuItemPermission = generateAccessControlConfig(false);

function generateAccessControlConfig(value: boolean): MenuItemPermission {
  return EnumHelpers.getKeys(PermissionTypeDto).reduce((acc, key) => ({ ...acc, [key]: value }), {} as MenuItemPermission);
}

@Injectable({
  providedIn: "root"
})
export class UserPermissionService {
  private profileSubscriptionId = "";
  private profileLogoutTooltip = "";
  private activationTransactionsReportName = "";
  private posSettingsConfiguration: AccountPosSystemSettingsConfiguration | null = null;
  private userPermissionsSrc$ = new BehaviorSubject<MenuItemPermission | null>(null);

  readonly lockedFeaturesMap = new Map<PermissionTypeDto, SubscriptionPlanShortGetDto>();
  readonly externalFeaturesMap = new Map<PermissionTypeDto, ExternalFeatureWithDependencyGetDto>();
  readonly userPermission$: Observable<MenuItemPermission> = this.userPermissionsSrc$.pipe(filter((config) => !!config));
  readonly ready$: Observable<boolean> = this.userPermissionsSrc$.pipe(
    switchMap((config) =>
      config
        ? of(true)
        : this.initialize().pipe(
            tap((config) => this.userPermissionsSrc$.next(config)),
            map((v) => !!v)
          )
    )
  );

  constructor(
    @Inject(ROUTE_PERMISSIONS_MAP) private routePermissionsMap: Record<string, PermissionTypeDto>,
    private accountService: AccountService,
    private appConfig: AppConfigService,
    private authService: AuthBaseService,
    private companyService: CompanyService,
    private currentAccountService: CurrentAccountService,
    private platformService: PlatformService,
    private subscriptionPlanService: SubscriptionPlanService
  ) {}

  get customTransactionsReportName(): string {
    return this.activationTransactionsReportName;
  }

  get logoutTooltip(): string {
    return this.profileLogoutTooltip;
  }

  get subscriptionId(): string {
    return this.profileSubscriptionId;
  }

  get isVimeoTemplateAvailable(): boolean {
    return false; // temporary disable, fixme
  }

  hasRoutePermission(route: string): boolean {
    const technicalRoutes = ["/pending-registration", "/access-denied"];

    if (this.isPermissionCheckSkipped() || technicalRoutes.includes(route)) {
      return true;
    }

    const routeKey = this.parseRoute(route);
    const permissionKey = this.routePermissionsMap[routeKey];
    const config = this.userPermissionsSrc$.value;
    const hasPermission = permissionKey && config && config[permissionKey];

    return !!hasPermission;
  }

  hasPermission(permission: PermissionTypeDto): boolean {
    if (this.isPermissionCheckSkipped()) {
      return true;
    }

    const config = this.userPermissionsSrc$.value;
    const hasPermission = config && config[permission];

    return !!hasPermission;
  }

  hasAtLeastOnePermission(permissions: PermissionTypeDto[]): boolean {
    return permissions.some((permission) => this.hasPermission(permission));
  }

  updatePermissionsByPosSettingsUpdates(posSettingsConfiguration: AccountPosSystemSettingsConfiguration): void {
    const permissions = this.userPermissionsSrc$.value;

    this.posSettingsConfiguration = posSettingsConfiguration;

    if (permissions) {
      this.userPermissionsSrc$.next(this.applyPosSettings(permissions));
    }
  }

  reload(): Observable<void> {
    if (!this.authService.isLoggedIn || this.isPermissionCheckSkipped()) {
      return of(undefined);
    }

    return this.getMenuItemAvailabilityParams().pipe(
      take(1),
      map((params) => this.applyMenuItemAvailabilityParams(params)),
      tap((config) => this.userPermissionsSrc$.next(this.applyPosSettings(config))),
      map(() => undefined)
    );
  }

  loadPermissionMappings(category: PermissionCategoryDto): Observable<PermissionMappingGetDto[]> {
    return this.subscriptionPlanService.getPermissionMappingsByCategory(category).pipe(
      tap((permissionMapping) => {
        permissionMapping.forEach((mapping) => {
          this.lockedFeaturesMap.set(mapping.permission, mapping.minPlan);
        });
      })
    );
  }

  private parseRoute(route: string): string {
    const path = route.split("?")[0].split(";")[0].split("#")[0];
    // removes the entire segment starting from the first digit part to the end of the URL.
    // '/aaa/bbb/111/ccc/2222' -> '/aaa/bbb'
    const resultPath = path.replace(/\/\d+.*$/, "");

    return resultPath;
  }

  private isPermissionCheckSkipped(): boolean {
    const isLocationManager: boolean = this.accountService.isUserInRole(RoleTypes.LocationManager);

    return (
      this.accountService.isUserRequireMinRole(RoleTypes.PartnerAdmin) || (!this.accountService.user?.doneQuickSetup && !isLocationManager)
    );
  }

  private initialize(): Observable<MenuItemPermission> {
    if (!this.authService.isLoggedIn || this.isPermissionCheckSkipped()) {
      return of(allowAllAccessControlConfig);
    }

    return this.accountService.userChanged$.pipe(
      filter((v) => !!v),
      tap((profile) => {
        const { name, family_name, subscriptionId } = profile;
        this.profileLogoutTooltip = `${name} ${family_name}`;
        this.profileSubscriptionId = subscriptionId || "";
      }),
      switchMap(() => this.getMenuItemAvailabilityParams()),
      map((params: MenuItemAvailabilityParams) => this.applyMenuItemAvailabilityParams(params)),
      catchError((error) => {
        console.error("Error in userPermissionService.initialize():", error);
        return of(disabledAccessControlConfig);
      })
    );
  }

  private applyMenuItemAvailabilityParams(params: MenuItemAvailabilityParams): MenuItemPermission {
    this.updateActivationTransactionsReportName(params);
    this.updateExternalFeaturesMapping(params.externalFeatures);

    return this.buildMenuItemPermissions(params.permissions);
  }

  private applyPosSettings(permissions: MenuItemPermission): MenuItemPermission {
    if (!this.posSettingsConfiguration) {
      return permissions;
    }

    const hasPosReceiptSettings = this.getIsReceiptPromotionEnabled(permissions, this.posSettingsConfiguration);

    return {
      ...permissions,
      [PermissionTypeDto.PosReceiptSettings]: hasPosReceiptSettings,
      [PermissionTypeDto.PrintReceipt]: hasPosReceiptSettings
    };
  }

  private parsePermissionsByContext(permissions: PermissionTypeDto[] | null): MenuItemPermission {
    const config = new Map<PermissionTypeDto, boolean>(Object.entries(disabledAccessControlConfig) as [PermissionTypeDto, boolean][]);

    (permissions || []).forEach((feature) => {
      config.set(feature, true);
    });

    allowedClientFeatures.forEach((feature) => {
      config.set(feature, true);
    });

    // todo: be better switch config to Map?
    return [...config.entries()].reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {} as MenuItemPermission);
  }

  private getMenuItemAvailabilityParams(): Observable<MenuItemAvailabilityParams> {
    return combineLatest([
      this.companyService.getPermissionsByCategory(PermissionCategoryDto.Core).pipe(map(({ permissions }) => permissions)),
      this.currentAccountService.account$,
      this.platformService.getAllAccountExternalFeatures(),
      this.loadPermissionMappings(PermissionCategoryDto.Core)
    ]).pipe(
      map(([permissions, account, externalFeatures]) => ({
        permissions,
        account,
        externalFeatures
      }))
    );
  }

  private updateActivationTransactionsReportName(params: MenuItemAvailabilityParams): void {
    const configuration: AccountConfigurationDto = params.account.configuration;
    const { reportName } = configuration.reporting?.activationTransactions || {};

    this.activationTransactionsReportName = reportName || "";
  }

  private updateExternalFeaturesMapping(externalFeatures: ExternalFeatureWithDependencyGetDto[]): void {
    externalFeatures.forEach((feature) => {
      this.externalFeaturesMap.set(feature.permission, feature);
    });
  }

  private buildMenuItemPermissions(permissions: PermissionTypeDto[]): MenuItemPermission {
    const contextConfig = this.parsePermissionsByContext(permissions);

    const config: MenuItemPermission = {
      ...contextConfig,

      // developers access:
      [PermissionTypeDto.Developers]: !this.appConfig.isProduction
    };

    return config;
  }

  private getIsReceiptPromotionEnabled(permissions: MenuItemPermission, posSystemSettings: AccountPosSystemSettingsConfiguration): boolean {
    const hasPosIntegration = this.isPermissionCheckSkipped() || !!permissions[PermissionTypeDto.PosIntegration];

    return hasPosIntegration && !!posSystemSettings?.isReceiptPromotionEnabled;
  }
}
